import { useEffect, useMemo, useRef, useState } from 'react';
import { useFormik } from 'formik';
import {
    Alert, Autocomplete, Box, Button, Grid2 as Grid, Paper, Stack, TextField, Typography
} from '@mui/material';
import { useAuth } from '../../auth/AuthProvider';
import SectionCard from '../../components/SectionCard';
import { getCountryOptions } from '../../configs/profile';
import { updateOrganizationAddress } from '../../services/organization';
import {
    buildOrganizationAddressPatch, organizationAddressSchema, organizationAddressValues
} from '../../utils/organization';
import OrganizationLogo from './OrganizationLogo';
import i18n, { t } from '../../i18n';

export default function Organization() {
    const { user, role, updateOrganizationAccount } = useAuth();
    const account = role.account;
    const [initial, setInitial] = useState(() => organizationAddressValues(account));
    const [notice, setNotice] = useState(null);
    const active = useRef(true);
    const lock = useRef(false);
    useEffect(() => { active.current = true; return () => { active.current = false; }; }, []);
    const language = i18n.resolvedLanguage;
    const countries = useMemo(() => {
        const options = getCountryOptions(language);
        if (initial.country && !options.some((option) => option.value === initial.country)) {
            options.push({ value: initial.country, label: initial.country });
        }
        return options;
    }, [language, initial.country]);
    const formik = useFormik({
        initialValues: initial,
        validationSchema: organizationAddressSchema(initial),
        onSubmit: async (values, { resetForm }) => {
            if (lock.current) return;
            lock.current = true;
            setNotice(null);
            try {
                const payload = buildOrganizationAddressPatch(values, initial);
                if (!Object.keys(payload).length) {
                    resetForm({ values: initial });
                    return;
                }
                const address = await updateOrganizationAddress({ user, role }, payload);
                updateOrganizationAccount({ address }, role.uuid);
                if (!active.current) return;
                const saved = { ...initial, ...address };
                setInitial(saved);
                resetForm({ values: saved });
                setNotice({ severity: 'success', text: 'A morada foi guardada.' });
            } catch {
                if (active.current) setNotice({ severity: 'error', text: 'Não foi possível guardar a morada. Tente novamente.' });
            } finally { lock.current = false; }
        }
    });
    const field = (name, label, maxLength, autoComplete) => <TextField
        fullWidth name={name} label={t(label)} value={formik.values[name]}
        onChange={formik.handleChange} onBlur={formik.handleBlur} disabled={formik.isSubmitting}
        autoComplete={autoComplete} inputProps={{ maxLength }}
        error={Boolean(formik.touched[name] && formik.errors[name])}
        helperText={t(formik.touched[name] && formik.errors[name])}
    />;
    return <Box sx={{ maxWidth: 1280, mx: 'auto' }}>
        <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 3 }, mb: 3, borderRadius: 3 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2.5}
                alignItems={{ xs: 'flex-start', sm: 'center' }}>
                <OrganizationLogo />
                <Box sx={{ minWidth: 0 }}>
                    <Typography component="h2" variant="h2" color="primary.main" sx={{ overflowWrap: 'anywhere' }}>
                        {account.name || t('Organização')}
                    </Typography>
                    <Typography color="text.secondary" sx={{ mt: 0.75 }}>{t('Dados da organização')}</Typography>
                </Box>
            </Stack>
        </Paper>
        <Grid container spacing={3} alignItems="flex-start">
            <Grid size={{ xs: 12, lg: 4 }}>
                <SectionCard title="Identificação">
                    <Stack spacing={2.5}>
                        <TextField fullWidth label={t('Nome da organização')} value={account.name || ''}
                            InputProps={{ readOnly: true }} helperText={t('Nome registado no ConnectedHeart.')} />
                        {account.email && <TextField fullWidth label={t('Email geral')} value={account.email}
                            InputProps={{ readOnly: true }} />}
                        {account.phone && <TextField fullWidth label={t('Telefone geral')} value={account.phone}
                            InputProps={{ readOnly: true }} />}
                    </Stack>
                </SectionCard>
            </Grid>
            <Grid size={{ xs: 12, lg: 8 }}>
                <Stack component="form" noValidate onSubmit={formik.handleSubmit} spacing={3}>
                    <SectionCard title="Morada">
                        <Grid container spacing={2.5}>
                            <Grid size={12}>{field('street', 'Morada', 200, 'street-address')}</Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>{field('zip', 'Código postal', 15, 'postal-code')}</Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>{field('city', 'Localidade', 30, 'address-level2')}</Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>{field('province', 'Distrito / região', 30, 'address-level1')}</Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Autocomplete options={countries}
                                    value={countries.find((option) => option.value === formik.values.country) || null}
                                    getOptionLabel={(option) => option.label}
                                    isOptionEqualToValue={(a, b) => a.value === b.value}
                                    disabled={formik.isSubmitting} disableClearable={Boolean(initial.country)}
                                    onChange={(_, option) => formik.setFieldValue('country', option?.value || '')}
                                    renderInput={(params) => <TextField {...params} label={t('País')}
                                        onBlur={() => formik.setFieldTouched('country', true)}
                                        error={Boolean(formik.touched.country && formik.errors.country)}
                                        helperText={t(formik.touched.country && formik.errors.country)} />}
                                />
                            </Grid>
                        </Grid>
                    </SectionCard>
                    {notice && <Alert severity={notice.severity}>{t(notice.text)}</Alert>}
                    <Stack direction="row" spacing={1.5} justifyContent="flex-end">
                        <Button disabled={formik.isSubmitting || !formik.dirty} onClick={() => {
                            formik.resetForm(); setNotice(null);
                        }}>{t('Cancelar alterações')}</Button>
                        <Button type="submit" variant="contained" disabled={formik.isSubmitting || !formik.dirty}>
                            {t(formik.isSubmitting ? 'A guardar…' : 'Guardar alterações')}
                        </Button>
                    </Stack>
                </Stack>
            </Grid>
        </Grid>
    </Box>;
}
